import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { updateFormData, saveDossier } from "../../redux/dispatch";
import useRubriqueNav from "../../hooks/useRubriqueNav";
import RubriqueNav from "../shared/RubriqueNav";
import useConfidence from "../../hooks/useConfidence";
import "./ResultatsBilan.css";
import SaveLoader from "../shared/SaveLoader";
import TabImagerie from "./tabs/TabImagerie";
import TabBiologie from "./tabs/TabBiologie";
import toast from "react-hot-toast";
import { API_BASE } from "../../config/api";
import noImage from "../../assets/no-image.svg";

const RUBRIQUE = "resultats_bilan";
const TABS = [
  {
    id: "imagerie",
    label: "Imagerie",
    subItems: [
      { id: "radio_standard", label: "Radiographies standards" },
      { id: "echographie",    label: "Échographies" },
      { id: "tdm",            label: "TDM" },
      { id: "irm",            label: "IRM" },
      { id: "autre_radio",    label: "Autre examen radiologique" },
    ],
  },
 {
    id: "biologie",
    label: "Biologie",
    subItems: [
      { id: "hema_biochimie",       label: "Hématologie & Biochimie"},
      { id: "coag_groupe",          label: "Coagulation & Groupe sanguin"},
      { id: "infect_marqueurs",     label: "Infectieux/microbiologie & Marqueurs tumoraux"},
      { id: "cyto_myelo",           label: "Cytologie & Myélogramme(s)"},
      { id: "lcr_histo",            label: "LCR & Histologie"},
      { id: "autres_bio",           label: "Autres examens biologiques"},
    ],
  },
];
const INITIAL = { imagerie: {}, biologie: {} };

const EXAMENS_KEYS = ["radio_standard", "echographie", "tdm", "irm", "autre_radio"];

const normalizeImagerie = (saved) => {
  if (!saved) return {};
  const result = { ...saved };
  EXAMENS_KEYS.forEach((key) => {
    const resultats = saved[`${key}_resultats`];
    result[`${key}_resultats`] = Array.isArray(resultats) ? resultats : [];
  });
  return result;
};

const ResultatsBilan = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id }   = useParams();
  const [saving, setSaving] = useState(false);

  const rawImages = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.images || []);
  const IMAGES = rawImages.length > 0
    ? rawImages.map((img) => `${API_BASE}/${img.chemin_image}`)
    : [noImage];

  const { currentDossier, list } = useSelector((s) => s.dossiers);
  const dossier = currentDossier || list.find((d) => d.id === id) || null;

  const savedImagerie = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.imagerie || {});
  const savedBiologie = useSelector((s) => s.formulaires.data?.[id]?.[RUBRIQUE]?.biologie || {});

  const [imagerie, setImagerie] = useState(normalizeImagerie(savedImagerie));
  const [biologie, setBiologie]   = useState({ ...INITIAL.biologie, ...savedBiologie });
  const [activeTab, setActiveTab] = useState("imagerie");
  const [activeSub, setActiveSub] = useState({ imagerie: "radio_standard", biologie: "hema_biochimie" });
  const [imgIndex, setImgIndex]   = useState(0);

  const imagerieRef = useRef(imagerie);
  const biologieRef = useRef(biologie);
  useEffect(() => { imagerieRef.current = imagerie; }, [imagerie]);
  useEffect(() => { biologieRef.current = biologie; }, [biologie]);

  useEffect(() => {
    setImagerie(normalizeImagerie(savedImagerie));
    setBiologie({ ...INITIAL.biologie, ...savedBiologie });
  }, [id]);

// APRÈS
const { goNext, goPrev, goTo, suivante, precedente, indexCourant, total } = useRubriqueNav(RUBRIQUE);
  // ── Confidence ───────────────────────────────────────────────────────────────
  const { getConfidence } = useConfidence(id, RUBRIQUE);
  const gcImg = (field) => getConfidence("imagerie", field);
  const gcBio = (field) => getConfidence("biologie", field);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const makeOnChange = (setter) => (field, value) =>
    setter((prev) => ({ ...prev, [field]: value }));

  const handleSave = () => {
    dispatch(updateFormData(id, RUBRIQUE, "imagerie", imagerieRef.current));
    dispatch(updateFormData(id, RUBRIQUE, "biologie", biologieRef.current));
  };

  useEffect(() => {
    return () => { handleSave(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveAll = async () => {
    setSaving(true);
    dispatch(updateFormData(id, RUBRIQUE, "imagerie", imagerie));
    dispatch(updateFormData(id, RUBRIQUE, "biologie", biologie));
    try {
      const result = await dispatch(saveDossier());
      toast.success(result.message);
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setImagerie(normalizeImagerie(savedImagerie));
    setBiologie({ ...INITIAL.biologie, ...savedBiologie });
  };

  if (!dossier) {
    return (
      <div className="rb-no-dossier">
        <p>Aucun dossier sélectionné.</p>
        <button onClick={() => navigate("/")}>Retour à la liste</button>
      </div>
    );
  }

  return (
    <div className="rb-page">
      {saving && <SaveLoader />}

      <header className="rb-header">
        <div className="rb-header-left">
          <button className="rb-back-btn" onClick={() => navigate("/")}>← Retour à la liste</button>
          <div>
            <h1 className="rb-title">Dossier {dossier.numero_dossier}</h1>
            <p className="rb-subtitle">Patient : {dossier.patient} &nbsp;|&nbsp; Service : {dossier.service}</p>
          </div>
        </div>
        <button className="rb-save-btn" onClick={handleSaveAll} disabled={saving}>
          Sauvegarder les modifications
        </button>
      </header>

      <div className="rb-body">
        <aside className="rb-image-panel">
          <div className="rb-image-header">
            <span className="rb-rubrique-label">Résultats du Bilan Initial</span>
            <span className="rb-image-counter">Image {imgIndex + 1} / {IMAGES.length}</span>
          </div>
          <div className="rb-image-viewer">
            <img src={IMAGES[imgIndex]} alt={`Page ${imgIndex + 1}`} className="rb-image" />
          </div>
          <div className="rb-image-nav">
            <button className="rb-nav-btn" onClick={() => setImgIndex((i) => Math.max(0, i - 1))} disabled={imgIndex === 0}>‹</button>
            <div className="rb-image-dots">
              {IMAGES.map((_, i) => (
                <span key={i} className={`rb-dot ${i === imgIndex ? "rb-dot--active" : ""}`} onClick={() => setImgIndex(i)} />
              ))}
            </div>
            <button className="rb-nav-btn" onClick={() => setImgIndex((i) => Math.min(IMAGES.length - 1, i + 1))} disabled={imgIndex === IMAGES.length - 1}>›</button>
          </div>
        </aside>

        <main className="rb-form-panel">
         <nav className="rb-tabs">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const hasSubItems = !!tab.subItems;
            return (
              <div key={tab.id} className={`rb-tab-wrapper ${isActive ? "rb-tab-wrapper--active" : ""}`}>
                <button
                  className={`rb-tab ${isActive ? "rb-tab--active" : ""}`}
                  onClick={() => {
                    setActiveTab(tab.id);
                    if (hasSubItems && !activeSub[tab.id]) {
                      setActiveSub((prev) => ({ ...prev, [tab.id]: tab.subItems[0].id }));
                    }
                  }}>
                  {tab.label}
                  {hasSubItems && <span className="rb-tab-chevron">▾</span>}
                </button>
                {hasSubItems && (
                  <div className="rb-dropdown">
                    {tab.subItems.map((sub) => (
                      <button key={sub.id}
                        className={`rb-dropdown-item ${activeSub[tab.id] === sub.id && isActive ? "rb-dropdown-item--active" : ""}`}
                        onClick={() => {
                          setActiveTab(tab.id);
                          setActiveSub((prev) => ({ ...prev, [tab.id]: sub.id }));
                        }}>
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

          <div className="rb-tab-content">
            <div className="rb-content-title">
              {(() => {
                const currentTab = TABS.find((t) => t.id === activeTab);
                const subLabel = currentTab?.subItems
                  ? currentTab.subItems.find((s) => s.id === activeSub[activeTab])?.label
                  : null;
                return subLabel ? (
                  <>
                    <span className="rb-content-title__parent">{currentTab.label}</span>
                    <span className="rb-content-title__sep"> › </span>
                    <span className="rb-content-title__child">{subLabel}</span>
                  </>
                ) : (
                  <span className="rb-content-title__child">{currentTab?.label}</span>
                );
              })()}
            </div>
            {activeTab === "imagerie" && (
              <TabImagerie form={imagerie} onChange={makeOnChange(setImagerie)} getConfidence={gcImg} activeSub={activeSub.imagerie} />
            )}
           {activeTab === "biologie" && (
              <TabBiologie form={biologie} onChange={makeOnChange(setBiologie)} getConfidence={gcBio} activeSub={activeSub.biologie} />
            )}
          </div>

{/*           <div className="rb-form-actions">
            <button className="rb-btn-save" onClick={handleSave}>Enregistrer</button>
            <button className="rb-btn-cancel" onClick={handleCancel}>Annuler</button>
          </div> */}


<RubriqueNav goNext={goNext} goPrev={goPrev} goTo={goTo} suivante={suivante} precedente={precedente} indexCourant={indexCourant} total={total} />        </main>
      </div>
    </div>
  );
};

export default ResultatsBilan;